"use client";

// ============================================================
// CLUBHUB – Assistent „Bestätigungen erstellen“
// ============================================================
// Der Januar-Moment: alle Spender eines Jahres mit unbestätigten
// Spenden, je Person Sammel- oder Einzelbestätigung, Vorschau des
// Blatts, ausstellen, versenden. In ClubDesk waren das zwei
// Wochenenden Serienbrief.
//
//   1 Spender    wer, in welcher Form — mit dem Hinweis, wo die
//                Bestätigung Pflicht ist und wo der Kontoauszug genügt
//   2 Vorschau   was entsteht, mit vorläufiger Nummer; jedes Blatt
//                lässt sich vorher ansehen
//   3 Versand    ausgestellt — jetzt per Mail bzw. als Brief
//
// Das Jahr steht in der Adresse (`?jahr=`), die Wahl je Person im
// Browser. Die Regeln (Sachspenden immer einzeln, Sammel erst ab
// zwei Geldzuwendungen) sind dieselben wie in `store.stelleAus`.
// ============================================================

import { useMemo, useState, useTransition } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowLeft, ArrowRight, Eye, FileCheck2, Mail, MapPinOff, Printer, Send } from "lucide-react";
import { Button, Card, CardContent, Checkbox, EmptyState, buttonVariants, confirmDialog, toast } from "@/components/ui";
import { Schiene, Zaehlknopf } from "@/components/liste";
import { Seitenblatt } from "@/components/ui/sheet";
import { TonBadge } from "@/components/mitglieder/status-badge";
import { cn, formatDate } from "@/lib/utils";
import { formatDecimal } from "@/lib/number-format";
import { kontoauszugGenuegt } from "@/lib/spenden/auswertung";
import { BELEGFORM, euro } from "@/lib/spenden/format";
import type { Ausstellauftrag, Belegform, Kandidat, Pflichtangaben, Posten, SpendeView } from "@/lib/spenden/views";
import { bestaetigungenAusstellen, bestaetigungenVersenden } from "@/app/(app)/spenden/actions";
import { Belegblatt, type BelegblattDaten } from "./belegblatt";
import { ArtBadge } from "./kleinteile";

type Geplant = {
  schluessel: string;
  contactId: string;
  name: string;
  form: Belegform;
  spenden: SpendeView[];
  betrag: number;
  nummer: string;
  versand: "EMAIL" | "POST";
};

type Ausgestellt = { ids: string[]; nummern: string[]; geplant: Geplant[] };

const SCHRITTE = ["Spender", "Vorschau", "Versand"] as const;

function plane(kandidaten: Kandidat[], gewaehlt: Set<string>, sammel: Record<string, boolean>, ausstellJahr: number, naechste: number): Geplant[] {
  const liste: Geplant[] = [];
  let lfd = naechste;
  const nummer = () => `ZB-${ausstellJahr}-${String(lfd++).padStart(4, "0")}`;
  for (const k of kandidaten) {
    if (!gewaehlt.has(k.contactId)) continue;
    if (sammel[k.contactId] && k.geld.length > 1) {
      liste.push({ schluessel: `${k.contactId}-sammel`, contactId: k.contactId, name: k.name, form: "SAMMEL", spenden: k.geld, betrag: summe(k.geld), nummer: nummer(), versand: k.versand });
    } else {
      for (const s of k.geld) liste.push({ schluessel: s.id, contactId: k.contactId, name: k.name, form: "GELD", spenden: [s], betrag: s.amount, nummer: nummer(), versand: k.versand });
    }
    for (const s of k.sach) liste.push({ schluessel: s.id, contactId: k.contactId, name: k.name, form: "SACH", spenden: [s], betrag: s.amount, nummer: nummer(), versand: k.versand });
  }
  return liste;
}

function summe(spenden: { amount: number }[]) {
  return Math.round(spenden.reduce((s, d) => s + d.amount, 0) * 100) / 100;
}

export function BestaetigungAssistent({
  jahr,
  heute,
  jahre,
  kandidaten,
  pflicht,
  naechsteNummer,
  nurPerson,
  mailVersand = true,
}: {
  jahr: number;
  heute: string;
  jahre: { jahr: number; personen: number; spenden: number }[];
  kandidaten: Kandidat[];
  pflicht: Pflichtangaben;
  naechsteNummer: number;
  nurPerson: string | null;
  /** Verschickt clubhub Mails mit Anhang selbst? Sonst laedt man die PDFs und vermerkt nur den Postversand. */
  mailVersand?: boolean;
}) {
  const router = useRouter();
  const ausstellJahr = Number(heute.slice(0, 4));
  const grenze = pflicht.vereinfachtBis;
  const [schritt, setSchritt] = useState(0);
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(
    () => new Set(kandidaten.filter((k) => k.adresseVollstaendig && (!nurPerson || k.contactId === nurPerson)).map((k) => k.contactId)),
  );
  const [sammel, setSammel] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(kandidaten.map((k) => [k.contactId, pflicht.sammelVorgabe && k.geld.length > 1])),
  );
  const [nurPflicht, setNurPflicht] = useState(false);
  const [vorschau, setVorschau] = useState<Geplant | null>(null);
  const [ergebnis, setErgebnis] = useState<Ausgestellt | null>(null);
  const [versendet, setVersendet] = useState<Record<"EMAIL" | "POST", boolean>>({ EMAIL: false, POST: false });
  const [laeuft, starte] = useTransition();

  const pflichtig = (k: Kandidat) => k.spenden.some((s) => !kontoauszugGenuegt(s, grenze));
  const sichtbar = kandidaten.filter((k) => !nurPflicht || pflichtig(k));
  const plan = useMemo(() => plane(kandidaten, gewaehlt, sammel, ausstellJahr, naechsteNummer), [kandidaten, gewaehlt, sammel, ausstellJahr, naechsteNummer]);
  const planSumme = summe(plan.map((p) => ({ amount: p.betrag })));
  const ohneAdresse = kandidaten.filter((k) => !k.adresseVollstaendig);

  function umschalten(id: string, an: boolean) {
    setGewaehlt((alt) => {
      const neu = new Set(alt);
      if (an) neu.add(id);
      else neu.delete(id);
      return neu;
    });
  }

  function alleWaehlen(an: boolean) {
    setGewaehlt(an ? new Set(sichtbar.filter((k) => k.adresseVollstaendig).map((k) => k.contactId)) : new Set());
  }

  function blattDaten(g: Geplant): BelegblattDaten {
    const sortiert = [...g.spenden].sort((a, b) => a.date.localeCompare(b.date));
    const letzte = sortiert[sortiert.length - 1]!;
    const k = kandidaten.find((x) => x.contactId === g.contactId)!;
    const posten: Posten[] = sortiert.map((s) => ({ donationId: s.id, date: s.date, kind: s.kind, amount: s.amount }));
    return {
      form: g.form,
      nummer: null,
      aussteller: pflicht.aussteller,
      empfaenger: k.anschrift,
      betrag: g.betrag,
      von: g.form === "SAMMEL" ? `${jahr}-01-01` : sortiert[0]!.date,
      bis: g.form === "SAMMEL" ? (jahr === ausstellJahr ? letzte.date : `${jahr}-12-31`) : sortiert[0]!.date,
      verzicht: g.form === "GELD" && sortiert[0]!.kind === "WAIVER",
      sach: g.form === "SACH" ? sortiert[0]!.sach : null,
      posten,
      ausgestelltAm: heute,
      vorschau: true,
    };
  }

  async function ausstellen() {
    const ja = await confirmDialog({
      title: `${plan.length} ${plan.length === 1 ? "Bestätigung" : "Bestätigungen"} ausstellen?`,
      description: `clubhub vergibt die Nummern ${plan[0]?.nummer} bis ${plan[plan.length - 1]?.nummer} und markiert die Spenden als bestätigt. Korrigieren lässt sich eine Bestätigung danach nur durch Widerruf und Neuausstellung.`,
      confirmLabel: "Ausstellen",
    });
    if (!ja) return;
    const auftraege: Ausstellauftrag[] = kandidaten
      .filter((k) => gewaehlt.has(k.contactId))
      .map((k) => ({ contactId: k.contactId, sammel: Boolean(sammel[k.contactId]), donationIds: k.spenden.map((s) => s.id) }));
    starte(async () => {
      const r = await bestaetigungenAusstellen(auftraege);
      if ("error" in r && r.error) {
        toast.error(r.error);
        return;
      }
      if (!("ids" in r)) return;
      toast.success(`${r.ids.length} ${r.ids.length === 1 ? "Bestätigung" : "Bestätigungen"} ausgestellt`, {
        description: [`${r.nummern[0]} bis ${r.nummern[r.nummern.length - 1]}`, "hinweis" in r ? r.hinweis : null].filter(Boolean).join(" · "),
      });
      // Die echten Nummern kommen vom Server — in derselben Reihenfolge wie geplant.
      setErgebnis({ ids: r.ids, nummern: r.nummern, geplant: plan.map((p, i) => ({ ...p, nummer: r.nummern[i] ?? p.nummer })) });
      setSchritt(2);
      router.refresh();
    });
  }

  function versenden(weg: "EMAIL" | "POST") {
    if (!ergebnis) return;
    const ids = ergebnis.geplant.map((g, i) => (g.versand === weg ? ergebnis.ids[i] : null)).filter((x): x is string => Boolean(x));
    starte(async () => {
      const r = await bestaetigungenVersenden(ids, weg);
      if ("error" in r && r.error) toast.error(r.error);
      else {
        setVersendet((v) => ({ ...v, [weg]: true }));
        toast.success(weg === "EMAIL" ? `${ids.length} Bestätigungen per Mail versendet` : `${ids.length} Briefe zum Drucken bereit`, {
          description: weg === "EMAIL" ? "Mit der Bestätigung als PDF im Anhang." : "Mit Anschriftfeld für den Fensterumschlag.",
        });
      }
    });
  }

  // ── Kopf: Jahr und Schritte ────────────────────────────────
  const kopf = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <ol className="flex items-center gap-2" aria-label="Schritte">
        {SCHRITTE.map((s, i) => (
          <li key={s} className="flex items-center gap-2">
            {i > 0 && <span className="bg-border h-px w-6" aria-hidden />}
            <span
              aria-current={i === schritt ? "step" : undefined}
              className={cn(
                "text-body-sm inline-flex items-center gap-1.5",
                i === schritt ? "text-text-primary font-medium" : i < schritt ? "text-text-secondary" : "text-text-tertiary",
              )}
            >
              <span
                className={cn(
                  "text-caption grid h-5 w-5 place-items-center rounded-full tabular-nums",
                  i === schritt ? "bg-text-primary text-text-inverse" : i < schritt ? "bg-success-subtle text-success" : "bg-bg-tertiary text-text-tertiary",
                )}
              >
                {i + 1}
              </span>
              {s}
            </span>
          </li>
        ))}
      </ol>
      {schritt === 0 && jahre.length > 0 && (
        <Schiene>
          {jahre.map((j) => (
            <Zaehlknopf
              key={j.jahr}
              label={`Spenden ${j.jahr}`}
              anzahl={j.personen}
              aktiv={j.jahr === jahr}
              titel={`${j.personen} Personen mit ${j.spenden} unbestätigten Spenden`}
              onClick={() => router.push(`/spenden/bestaetigungen/erstellen?jahr=${j.jahr}`)}
            />
          ))}
        </Schiene>
      )}
    </div>
  );

  if (kandidaten.length === 0 && !ergebnis) {
    return (
      <div className="space-y-4">
        {kopf}
        <EmptyState
          icon={FileCheck2}
          title={`Für ${jahr} ist alles bestätigt`}
          description={jahre.length > 0 ? "Für andere Jahre gibt es noch offene Spenden — oben wählen." : "Es gibt keine Spende einer bekannten Person ohne Bestätigung. Spenden ohne Namen lassen sich nicht bestätigen."}
          action={
            <Link href="/spenden/bestaetigungen" className={buttonVariants({ variant: "outline" })}>
              Zu den Bestätigungen
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {kopf}

      {/* ── 1 Spender ─────────────────────────────────────── */}
      {schritt === 0 && (
        <Card className="overflow-hidden">
          <div className="border-border flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-5 py-3">
            <Checkbox
              checked={sichtbar.filter((k) => k.adresseVollstaendig).every((k) => gewaehlt.has(k.contactId)) && gewaehlt.size > 0}
              onChange={(e) => alleWaehlen(e.target.checked)}
              label="Alle wählen"
            />
            <span className="flex-1" />
            <Schiene>
              <Zaehlknopf label="Alle" anzahl={kandidaten.length} aktiv={!nurPflicht} onClick={() => setNurPflicht(false)} />
              <Zaehlknopf
                label="Bestätigung nötig"
                anzahl={kandidaten.filter(pflichtig).length}
                aktiv={nurPflicht}
                titel={`Mindestens eine Spende über ${euro(grenze)}, bar, als Sache oder Verzicht`}
                onClick={() => setNurPflicht(true)}
              />
            </Schiene>
          </div>

          <ul className="divide-border-subtle divide-y">
            {sichtbar.map((k) => {
              const an = gewaehlt.has(k.contactId);
              const kannSammeln = k.geld.length > 1;
              const noetig = pflichtig(k);
              return (
                <li key={k.contactId} className={cn("flex flex-wrap items-start gap-x-4 gap-y-2 px-5 py-3.5", !k.adresseVollstaendig && "opacity-60")}>
                  <Checkbox
                    checked={an}
                    disabled={!k.adresseVollstaendig}
                    onChange={(e) => umschalten(k.contactId, e.target.checked)}
                    aria-label={`${k.name} auswählen`}
                    className="mt-0.5"
                  />
                  <div className="min-w-[12rem] flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link href={`/spenden/spender/${k.contactId}`} className="text-body-sm text-text-primary hover:text-accent font-medium">
                        {k.name}
                      </Link>
                      {noetig ? (
                        <TonBadge ton="warning">nötig</TonBadge>
                      ) : (
                        <span className="text-caption text-text-tertiary" title={`Jede Spende bis ${euro(grenze)} per Konto — der Kontoauszug genügt dem Finanzamt.`}>
                          Kontoauszug genügt
                        </span>
                      )}
                    </div>
                    <p className="text-caption text-text-tertiary mt-0.5">
                      {k.adresseVollstaendig ? (
                        <>
                          {k.anschrift.zeilen.join(", ")} ·{" "}
                          {k.versand === "EMAIL" ? (
                            <span className="inline-flex items-center gap-1">
                              <Mail className="h-3 w-3" aria-hidden />
                              {k.email}
                            </span>
                          ) : (
                            "per Post"
                          )}
                        </>
                      ) : (
                        <span className="text-danger inline-flex items-center gap-1">
                          <MapPinOff className="h-3 w-3" aria-hidden />
                          Anschrift unvollständig — gehört auf die Bestätigung. Erst bei der Person ergänzen.
                        </span>
                      )}
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {k.spenden.map((s) => (
                        <span key={s.id} className="bg-bg-secondary text-caption text-text-secondary inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 tabular-nums" title={s.purpose ?? undefined}>
                          {formatDate(s.date).slice(0, 6)} {euro(s.amount)}
                          {s.kind !== "MONEY" && <ArtBadge kind={s.kind} className="px-1.5 py-0" />}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 max-sm:w-full max-sm:justify-between">
                    {kannSammeln ? (
                      <Schiene>
                        <Zaehlknopf label="Sammel" anzahl={1} aktiv={Boolean(sammel[k.contactId])} onClick={() => setSammel((s) => ({ ...s, [k.contactId]: true }))} />
                        <Zaehlknopf label="Einzeln" anzahl={k.geld.length} aktiv={!sammel[k.contactId]} onClick={() => setSammel((s) => ({ ...s, [k.contactId]: false }))} />
                      </Schiene>
                    ) : (
                      <span className="text-caption text-text-tertiary">Einzelbestätigung</span>
                    )}
                    <span className="text-body-sm text-text-primary w-24 text-right font-medium tabular-nums">{euro(k.summe)}</span>
                  </div>
                  {k.sach.length > 0 && (
                    <p className="text-caption text-text-tertiary w-full pl-8">
                      {k.sach.length === 1 ? "Die Sachspende bekommt" : `Die ${k.sach.length} Sachspenden bekommen`} eine eigene Bestätigung über Sachzuwendungen — eine Sammelbestätigung gibt es nur für Geld.
                    </p>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="border-border bg-bg-secondary flex flex-wrap items-center gap-x-6 gap-y-2 border-t px-5 py-3">
            <span className="text-body-sm text-text-primary font-medium">
              {formatDecimal(gewaehlt.size)} {gewaehlt.size === 1 ? "Person" : "Personen"} · {formatDecimal(plan.length)} {plan.length === 1 ? "Bestätigung" : "Bestätigungen"}
            </span>
            <span className="text-body-sm text-text-secondary tabular-nums">{euro(planSumme)}</span>
            {ohneAdresse.length > 0 && <span className="text-caption text-danger">{ohneAdresse.length} ohne vollständige Anschrift</span>}
            <span className="flex-1" />
            <Button onClick={() => setSchritt(1)} disabled={plan.length === 0}>
              Weiter zur Vorschau
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </Card>
      )}

      {/* ── 2 Vorschau ────────────────────────────────────── */}
      {schritt === 1 && (
        <Card className="overflow-hidden">
          <CardContent className="space-y-1 p-5 pb-3">
            <p className="text-body-sm text-text-primary font-medium">
              Es entstehen {formatDecimal(plan.length)} {plan.length === 1 ? "Bestätigung" : "Bestätigungen"} über {euro(planSumme)}
            </p>
            <p className="text-caption text-text-tertiary">
              Ausgestellt am {formatDate(heute)} in {pflicht.aussteller.ort ?? "–"}, unterzeichnet von {pflicht.aussteller.signedBy ?? "–"}. Die Nummern sind vorläufig, bis ausgestellt ist.
            </p>
          </CardContent>
          <ul className="divide-border-subtle border-border-subtle divide-y border-t">
            {plan.map((g) => (
              <li key={g.schluessel}>
                <button type="button" onClick={() => setVorschau(g)} className="hover:bg-bg-secondary flex w-full items-center gap-4 px-5 py-3 text-left">
                  <span className="text-caption text-text-tertiary w-28 shrink-0 tabular-nums">{g.nummer}</span>
                  <span className="min-w-0 flex-1">
                    <span className="text-body-sm text-text-primary block truncate">{g.name}</span>
                    <span className="text-caption text-text-tertiary block">
                      {BELEGFORM[g.form].label}
                      {g.form === "SAMMEL" ? ` · ${g.spenden.length} Zuwendungen` : ` · ${formatDate(g.spenden[0]!.date)}`}
                      {" · "}
                      {g.versand === "EMAIL" ? "per Mail" : "per Post"}
                    </span>
                  </span>
                  <span className="text-body-sm text-text-primary shrink-0 font-medium tabular-nums">{euro(g.betrag)}</span>
                  <Eye className="text-text-tertiary h-4 w-4 shrink-0" aria-label="Vorschau" />
                </button>
              </li>
            ))}
          </ul>
          <div className="border-border bg-bg-secondary flex flex-wrap items-center gap-3 border-t px-5 py-3">
            <Button variant="ghost" onClick={() => setSchritt(0)}>
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Zurück
            </Button>
            <span className="flex-1" />
            {!pflicht.bereit && <span className="text-caption text-danger">Erst die Pflichtangaben des Vereins ergänzen.</span>}
            <Button onClick={ausstellen} loading={laeuft} disabled={!pflicht.bereit || plan.length === 0}>
              <FileCheck2 className="h-4 w-4" aria-hidden />
              {plan.length} {plan.length === 1 ? "Bestätigung" : "Bestätigungen"} ausstellen
            </Button>
          </div>
        </Card>
      )}

      {/* ── 3 Versand ─────────────────────────────────────── */}
      {schritt === 2 && ergebnis && (
        <Card className="overflow-hidden">
          <CardContent className="space-y-4 p-5">
            <div>
              <p className="text-heading-sm text-text-primary">
                {ergebnis.ids.length} {ergebnis.ids.length === 1 ? "Bestätigung ist" : "Bestätigungen sind"} ausgestellt
              </p>
              <p className="text-body-sm text-text-secondary mt-0.5 tabular-nums">
                {ergebnis.nummern[0]} bis {ergebnis.nummern[ergebnis.nummern.length - 1]} · {euro(summe(ergebnis.geplant.map((g) => ({ amount: g.betrag }))))}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {(["EMAIL", "POST"] as const).map((weg) => {
                const anzahl = ergebnis.geplant.filter((g) => g.versand === weg).length;
                const folgt = weg === "EMAIL" && !mailVersand;
                return (
                  <div key={weg} className="border-border rounded-xl border p-4">
                    <div className="flex items-center gap-2">
                      {weg === "EMAIL" ? <Mail className="text-text-tertiary h-4 w-4" aria-hidden /> : <Printer className="text-text-tertiary h-4 w-4" aria-hidden />}
                      <p className="text-body-sm text-text-primary font-medium">
                        {anzahl} {weg === "EMAIL" ? "per E-Mail" : "per Post"}
                      </p>
                    </div>
                    <p className="text-caption text-text-tertiary mt-1">
                      {folgt
                        ? "Der Mailversand kommt mit dem Bereich Nachrichten. Bis dahin: PDF je Bestätigung laden (Liste unten) und selbst senden."
                        : weg === "EMAIL"
                          ? "Mit der Bestätigung als PDF im Anhang, an die hinterlegte Adresse."
                          : mailVersand
                            ? "Alle Briefe in einem PDF, mit Anschriftfeld für den Fensterumschlag."
                            : "PDF je Bestätigung laden und drucken, dann hier den Versand vermerken."}
                    </p>
                    <Button
                      className="mt-3"
                      variant={versendet[weg] ? "outline" : "default"}
                      size="sm"
                      disabled={anzahl === 0 || versendet[weg] || folgt}
                      loading={laeuft}
                      onClick={() => versenden(weg)}
                    >
                      {weg === "EMAIL" ? <Send className="h-3.5 w-3.5" aria-hidden /> : <Printer className="h-3.5 w-3.5" aria-hidden />}
                      {versendet[weg] ? "Erledigt" : folgt ? "Folgt" : weg === "EMAIL" ? "Jetzt senden" : mailVersand ? "Briefe erzeugen" : "Postversand vermerken"}
                    </Button>
                  </div>
                );
              })}
            </div>
            <ul className="divide-border-subtle border-border-subtle max-h-72 divide-y overflow-y-auto border-t">
              {ergebnis.geplant.map((g, i) => (
                <li key={g.schluessel}>
                  <Link href={`/spenden/bestaetigungen/${ergebnis.ids[i]}`} className="hover:bg-bg-secondary flex items-center gap-4 px-1 py-2">
                    <span className="text-caption text-text-secondary w-28 shrink-0 tabular-nums">{g.nummer}</span>
                    <span className="text-body-sm text-text-primary min-w-0 flex-1 truncate">{g.name}</span>
                    <span className="text-caption text-text-tertiary">{BELEGFORM[g.form].label}</span>
                    <span className="text-body-sm shrink-0 tabular-nums">{euro(g.betrag)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
          <div className="border-border bg-bg-secondary flex flex-wrap items-center gap-3 border-t px-5 py-3">
            <span className="flex-1" />
            <Link href="/spenden/bestaetigungen" className={buttonVariants({ variant: "outline" })}>
              Zu den Bestätigungen
            </Link>
          </div>
        </Card>
      )}

      <Seitenblatt
        offen={Boolean(vorschau)}
        onSchliessen={() => setVorschau(null)}
        titel="Vorschau der Bestätigung"
        breite="900px"
        kopfZusatz={
          vorschau && (
            <div>
              <p className="text-body-sm text-text-primary font-medium">{vorschau.name}</p>
              <p className="text-caption text-text-tertiary">
                {BELEGFORM[vorschau.form].label} · vorläufig {vorschau.nummer}
              </p>
            </div>
          )
        }
        inhaltClassName="bg-bg-sheet min-h-0 flex-1 overflow-y-auto p-3 sm:p-5"
      >
        {vorschau && <Belegblatt d={blattDaten(vorschau)} />}
      </Seitenblatt>
    </div>
  );
}
